// src/pages/DashboardPage.tsx
import React, { useState } from 'react';
import config from '../config';

import {
    dashboardContainerStyle,
    headerStyle,
    headerLeft,
    welcomeTitle,
    userEmail,
    headerRight,
    profilePicStyle,
    logoutButtonStyle,
    contentAreaStyle,
    sidebarStyle,
    createButtonStyle,
    navStyle,
    navLinkStyle,
    mainContentStyle,
    sectionTitleStyle,
    tableStyle,
    thStyle,
    tdStyle,
    trStyle,
    actionButtonStyle,
    noDocsStyle,
} from './dashboardStyles';
import PopUpShare from './PopUpShare';

interface Project {
    project_id: number;
    name: string;
    description: string;
    created_at: string;
    updated_at: string;
    role: string;
    is_deleted?: string; // <--- ahora string
    deleted_at: string | null;
    is_shared?: string; 
}

interface DashboardPageProps {
    userProfile: any;
    userProjects: Project[];
    onLogout: () => void;
    onOpenProject: (projectId: number, name: string, description: string) => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ userProfile, userProjects, onLogout, onOpenProject }) => {
    const [projects, setProjects] = useState<Project[]>(userProjects);
    const [activeTab, setActiveTab] = useState<'projects' | 'trash' | 'shared' >('projects');
    const [sortField, setSortField] = useState<keyof Project>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [shareUrl, setShareUrl] = useState('');

    // Update projects if userProjects prop changes (e.g. after login)
    React.useEffect(() => {
        setProjects(userProjects);
    }, [userProjects]);

    const myProjects = projects.filter(p => p.is_deleted == '0' || p.is_deleted === undefined);
    const trashProjects = projects.filter(p => p.is_deleted == '1');
    const sharedProjects = projects.filter(p => p.is_shared == '1');

    const handleOpen = (projectId: number, name: string, description: string) => {
        onOpenProject(projectId, name, description);
        // alert(`Opening project: ${projectId}`);
        // Add your navigation or editor logic here
    };

    const handleCreateNew = async () => {
        const name = prompt('Enter project name:');
        if (!name) return;
        const description = prompt('Enter project description:') || '';

        try {
            const response = await fetch(`${config.API_BASE_URL}/auth/create-project.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    name,
                    description,
                    user_id: userProfile.id
                })
            });
            const data = await response.json();
            if (data.success && data.project) {
                setProjects([data.project, ...projects]);
                alert('Project created!');
            } else {
                alert('Error creating project: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            alert('Network error creating project');
        }
    };

    const handleDelete = async (projectId: number) => {
        if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;

        try {
            const response = await fetch(`${config.API_BASE_URL}/auth/delete-project.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    project_id: projectId,
                    user_id: userProfile.id
                })
            });
            const data = await response.json();
            if (data.success) {
                // setProjects(projects.filter(p => p.project_id !== projectId));
                setProjects(projects.map(p =>
                    p.project_id === projectId ? { ...p, is_deleted: '1', deleted_at: null } : p
                ));
                alert('Project deleted!');
            } else {
                alert('Error deleting project: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            alert('Network error deleting project');
        }
    };

    const handleShare = async (projectId: number) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/share/create-share-link.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ project_id: projectId })
            });
            const data = await response.json();
            if (data.success && data.token) {
                const shareUrl = `${window.location.origin + window.location.pathname}?share_token=${data.token}`;
                try {
                    // await navigator.clipboard.writeText(shareUrl);
                    setShareUrl(shareUrl);
                    setShowSharePopup(true);
                    console.log('Share URL copied to clipboard:', shareUrl);
                } catch (e) {
                    // ignore clipboard errors
                }
                // alert('Share URL copied to clipboard:\n' + shareUrl);
            } else {
                // alert('Error creating share link: ' + (data.message || 'Unknown error'));
                console.log('Error creating share link:', data.message || 'Unknown error');
                console.log('Response data:', data);
            }
        } catch (err) {
            // alert('Network error creating share link');
            console.log(`Network error creating share link: ${err}`);

        }
    };

    const handleRemoveShare = async (projectId: number) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/share/remove-share-link.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    project_id: projectId,
                    user_id: userProfile.id
                })
            });
            const data = await response.json();
            if (data.success) {
                setProjects(projects.map(p =>
                    p.project_id === projectId ? { ...p, is_shared: '0' } : p
                ));
                alert('Share removed!');
            } else {
                alert('Error removing share: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            alert('Network error removing share');
        }
    }

    const handleRestore = async (projectId: number) => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/auth/restore-project.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    project_id: projectId,
                    user_id: userProfile.id
                })
            });
            const data = await response.json();
            if (data.success) {
                setProjects(projects.map(p =>
                    p.project_id === projectId ? { ...p, is_deleted: '0', deleted_at: null } : p
                ));
                alert('Project restored!');
            } else {
                alert('Error restoring project: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            alert('Network error restoring project');
        }
    };

    const handleSort = (field: keyof Project) => {
        const sorted = [...projects].sort((a, b) => {
            const aValue = a[field] || '';
            const bValue = b[field] || '';

            if (aValue < bValue) {
                return sortOrder === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortOrder === 'asc' ? 1 : -1;
            }
            return 0;
        });
        setProjects(sorted);
        setSortField(field); // Track the current field being sorted
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const renderSortArrow = (field: keyof Project) => {
        if (sortField !== field) return null;
        return sortOrder === 'asc' ? ' ▲' : ' ▼';
    };

    const sortedProjects = React.useMemo(() => {
        let sortableProjects = [...myProjects];
        if (sortField !== null) {
            sortableProjects.sort((a, b) => {
                const aValue = a[sortField] || '';
                const bValue = b[sortField] || '';
                if (aValue < bValue) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableProjects;
    }, [myProjects, sortField, sortOrder]);

    return (
        <div style={dashboardContainerStyle}>
            <header style={headerStyle}>
                <div style={headerLeft}>
                    <h2 style={welcomeTitle}>Hello, {userProfile?.name || 'User'}!</h2>
                    <span style={userEmail}>{userProfile?.email}</span>
                </div>
                <div style={headerRight}>
                    {userProfile?.picture && (
                        <img src={userProfile.picture} alt="Profile" style={profilePicStyle} />
                    )}
                    <button onClick={onLogout} style={logoutButtonStyle}>Log Out</button>
                </div>
            </header>

            <div style={contentAreaStyle}>
                <div style={sidebarStyle}>
                    <button style={createButtonStyle} onClick={handleCreateNew}>
                        <span style={{ fontSize: '1.2em', marginRight: '5px' }}>+</span> New Project
                    </button>
                    <nav style={navStyle}>
                        <a
                            href="#"
                            style={{ ...navLinkStyle, fontWeight: activeTab === 'projects' ? 'bold' : 'normal' }}
                            onClick={() => setActiveTab('projects')}
                        >
                            My Projects
                        </a>
                        <a
                            href="#"
                            style={{ ...navLinkStyle, fontWeight: activeTab === 'shared' ? 'bold' : 'normal' }}
                            onClick={() => setActiveTab('shared')}
                        >
                            Shared
                        </a>
                        <a
                            href="#"
                            style={{ ...navLinkStyle, fontWeight: activeTab === 'trash' ? 'bold' : 'normal' }}
                            onClick={() => setActiveTab('trash')}
                        >
                            Trash
                        </a>
                    </nav>
                </div>

                <main style={mainContentStyle}>
                    {showSharePopup && (
                        <PopUpShare
                            title={"Share Project"}
                            description={"Everyone with the link can view this project. Users using this link will get a copy of the latest state of this project in their account. You can share this link with your collaborators or anyone you want to show your project to. \n To remove sharing, go to the dashboard -> Shared and click 'Remove share'."}
                            onClose={() => setShowSharePopup(false)}
                            url={shareUrl}
                        />
                            
                    )}
                    {activeTab === 'projects' ? (
                        <>
                            <h3 style={sectionTitleStyle}>My Projects</h3>
                            <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)', border: '1px solid #ddd' }}>
                                <table style={{ ...tableStyle, borderCollapse: 'collapse', width: '100%' }}>
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                        <tr>
                                            <th style={thStyle} onClick={() => handleSort('name')}>Name{renderSortArrow('name')}</th>
                                            <th style={thStyle} onClick={() => handleSort('description')}>Description{renderSortArrow('description')}</th>
                                            <th style={thStyle} onClick={() => handleSort('role')}>Role{renderSortArrow('role')}</th>
                                            <th style={thStyle} onClick={() => handleSort('created_at')}>Created{renderSortArrow('created_at')}</th>
                                            <th style={thStyle} onClick={() => handleSort('updated_at')}>Updated{renderSortArrow('updated_at')}</th>
                                            <th style={thStyle}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedProjects.map(project => (
                                            <tr key={project.project_id} style={trStyle}>
                                                <td style={tdStyle}>{project.name}</td>
                                                <td style={tdStyle}>{project.description}</td>
                                                <td style={tdStyle}>{project.role}</td>
                                                <td style={tdStyle}>{project.created_at}</td>
                                                <td style={tdStyle}>{project.updated_at}</td>
                                                <td style={tdStyle}>
                                                    <button onClick={() => handleOpen(project.project_id, project.name, project.description)} style={actionButtonStyle}>Open</button>
                                                    <button onClick={() => handleShare(project.project_id)} style={{ ...actionButtonStyle, backgroundColor: '#007bff' }}>Share</button>
                                                    {project.role === 'owner' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleDelete(project.project_id)}
                                                                style={{ ...actionButtonStyle, backgroundColor: '#202020ff' }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {myProjects.length === 0 && <p style={noDocsStyle}>You have no projects. Create a new one!</p>}
                        </>
                    ) : activeTab === 'shared' ? (
                        <>

                            <h3 style={sectionTitleStyle}>Trash</h3>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Name</th>
                                        <th style={thStyle}>Description</th>
                                        <th style={thStyle}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sharedProjects.map(project => (
                                        <tr key={project.project_id} style={trStyle}>
                                            <td style={tdStyle}>{project.name}</td>
                                            <td style={tdStyle}>{project.description}</td>
                                            <td style={tdStyle}>
                                                <button
                                                    onClick={() => handleRemoveShare(project.project_id)}
                                                    style={{ ...actionButtonStyle, backgroundColor: '#28a745' }}
                                                >
                                                    Remove share
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {sharedProjects.length === 0 && <p style={noDocsStyle}>No shared projects.</p>}

                        </>
                    ) : (
                        <>
                            <h3 style={sectionTitleStyle}>Trash</h3>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Name</th>
                                        <th style={thStyle}>Description</th>
                                        <th style={thStyle}>Deleted At</th>
                                        <th style={thStyle}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trashProjects.map(project => (
                                        <tr key={project.project_id} style={trStyle}>
                                            <td style={tdStyle}>{project.name}</td>
                                            <td style={tdStyle}>{project.description}</td>
                                            <td style={tdStyle}>{project.deleted_at}</td>
                                            <td style={tdStyle}>
                                                <button
                                                    onClick={() => handleRestore(project.project_id)}
                                                    style={{ ...actionButtonStyle, backgroundColor: '#28a745' }}
                                                >
                                                    Restore
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {trashProjects.length === 0 && <p style={noDocsStyle}>Trash is empty.</p>}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};
export default DashboardPage;